import { useEffect, useRef, useState } from "react";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import { useCartMutations } from "../../../services/hooks/useCartMutations";
import { useAppSelector } from "../../../services/hooks/useReduxHooks";
import { selectUseLoyaltyService } from "../../../services/redux/selectors/loyaltyServiceSelectors";
import { CartItemType } from "../../../services/redux/types/CartLoyaltyV2Types";
import { cartItemTypeToAddLineInput } from "../../../utils/cart/cartLineTypes";
import { cn } from "../../../utils/cn";
import {
  CUSTOMER_ANALYTICS_EVENT,
  ChangeCounterFrom,
  EditCartItemEnum,
} from "../../../utils/enums";
import { localizedNumber } from "../../../utils/help/localization/localization";
import { AddIcon, MinusIcon } from "../Icons/Icons";
import styles from "./CounterV2.module.scss";

type CounterV2Size = "sm" | "lg";

type CounterV2Layout = "horizontal" | "vertical";

export const CounterV2 = (props: {
  count: number;
  itemIndex?: number;
  data?: CartItemType;
  changeCounterView: ChangeCounterFrom;
  setCount?: (num: number) => void;
  isDisabled?: boolean;
  subitemId?: number;
  setDisableDelete?: (isDisabled: boolean) => void;
  size?: CounterV2Size;
  layout?: CounterV2Layout;
  hasShadow?: boolean;
  transparent?: boolean;
  /** When true and count is 0 (plus-only state), renders plus button outlined with primary icon. Defaults to false. */
  outlinedWhenZero?: boolean;
  includeBackground?: boolean;
  isPlusDisabled?: boolean;
  isMinusDisabled?: boolean;
  disabledButtonsColor?: "primary" | "gray";
  customOnClick?: (editType: EditCartItemEnum) => void;
}) => {
  const { disabledButtonsColor = "gray" } = props;
  const { editItem, editLineQuantity, removeItem } = useCartMutations();
  const useLoyaltyService = useAppSelector(selectUseLoyaltyService);
  const addCustomerAnalytics = useCustomerAnalytics();

  const [itemCount, setItemCount] = useState(props.count);
  const [hasSetInitialCount, setHasSetInitialCount] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingCountRef = useRef<number | null>(null);
  const isUpdatingRef = useRef(false);
  const exitAnimTimerRef = useRef<NodeJS.Timeout | null>(null);

  const size = props.size ?? "lg";
  const layout = props.layout ?? "horizontal";
  const hasShadow = props.hasShadow ?? false;
  const EXIT_ANIMATION_DURATION = 200;
  const includeBackground = props.includeBackground ?? false;

  // Sync external count changes. Trigger enter animation only if count arrives
  // as >0 while local itemCount is still 0 — meaning the add came from outside
  // the Counter (e.g. parent overlay click). Internal adds update itemCount
  // synchronously before Redux propagates, so this branch is never hit for them.
  useEffect(() => {
    setItemCount(props.count);

    if (!hasSetInitialCount) {
      setHasSetInitialCount(true);
    }
  }, [props.count]);

  useEffect(() => {
    return () => {
      if (exitAnimTimerRef.current) {
        clearTimeout(exitAnimTimerRef.current);
      }
    };
  }, []);

  const handleCountChange = (newCount: number) => {
    setItemCount(newCount);

    if (props.setDisableDelete) {
      props.setDisableDelete(true);
    }

    pendingCountRef.current = newCount;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const delay = newCount === 0 ? EXIT_ANIMATION_DURATION : 500;

    timerRef.current = setTimeout(() => {
      if (pendingCountRef.current !== null) {
        const finalCount = pendingCountRef.current;
        pendingCountRef.current = null;

        if (!isUpdatingRef.current) {
          isUpdatingRef.current = true;
          try {
            executeCounterAction(finalCount);
          } finally {
            isUpdatingRef.current = false;
          }
        }

        if (props.setDisableDelete) {
          props.setDisableDelete(false);
        }
      }
    }, delay);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const executeCounterAction = (count: number) => {
    if (!props.data) {
      return;
    }

    const lineId = (props.data as { lineId?: string }).lineId;
    const canEditByLineId = !!lineId;
    const canEditByIndex =
      props.itemIndex !== undefined && props.itemIndex !== -1;

    if (!canEditByLineId && !canEditByIndex) {
      return;
    }

    if (count === 0) {
      if (lineId) {
        removeItem({ lineId });
      } else {
        removeItem({ itemIndex: props.itemIndex! });
      }

      return;
    }

    if (lineId && useLoyaltyService) {
      editLineQuantity({ lineId, quantity: count });

      return;
    }

    const editInput = {
      ...cartItemTypeToAddLineInput(props.data),
      count,
    };

    if (lineId) {
      editItem({ lineId, input: editInput });
    } else {
      editItem({ itemIndex: props.itemIndex!, input: editInput });
    }
  };

  const changeCount = (editType: EditCartItemEnum) => {
    const isAddOnsContext =
      props.changeCounterView === ChangeCounterFrom.AddOns;
    const isItemViewContext =
      props.changeCounterView === ChangeCounterFrom.ItemView;
    const isMenuContext =
      props.changeCounterView === ChangeCounterFrom.CartView &&
      !props.setDisableDelete;

    if (isAddOnsContext || isItemViewContext) {
      const minCount = isAddOnsContext ? 0 : 1;

      if (editType === EditCartItemEnum.CounterIncChange) {
        if (props.setCount && props.count !== undefined) {
          props.setCount(props.count + 1);
        }
        addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.CHANGE_COUNTER, {
          state: "+",
          subitemId: props.subitemId,
          Screen: "ItemScreen",
        });
      } else {
        if (props.count !== undefined && props.count > minCount) {
          props.setCount && props.setCount(props.count - 1);
          addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.CHANGE_COUNTER, {
            state: "-",
            subitemId: props.subitemId,
            Screen: "ItemScreen",
          });
        }
      }
    } else if (props.data && props.itemIndex !== undefined) {
      let newCount = itemCount;

      if (editType === EditCartItemEnum.CounterIncChange) {
        if (isMenuContext) {
          addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.ADD_ITEM_FROM_MENU);
        }
        newCount = itemCount + 1;
      } else if (editType === EditCartItemEnum.CounterDecChange) {
        newCount = itemCount > 1 ? itemCount - 1 : 0;
      }

      handleCountChange(newCount);
    }
  };

  const isPlusOnlyState = itemCount === 0;
  const showOutlinedStyle =
    isPlusOnlyState && (props.outlinedWhenZero ?? false);

  const plusButton = (
    <button
      type="button"
      className={cn(
        styles.button,
        hasShadow && styles.shadow,
        showOutlinedStyle && styles.button_outlined,
        includeBackground && styles.button_on_background,
        disabledButtonsColor === "primary" && styles.primary_disabled
      )}
      aria-label="plus"
      role="plus-button"
      data-testid={`plus-${props.data?.itemDetails.item_name}`}
      id={"plusbutton" + props.itemIndex}
      onClick={() =>
        props.customOnClick
          ? props.customOnClick(EditCartItemEnum.CounterIncChange)
          : changeCount(EditCartItemEnum.CounterIncChange)
      }
      disabled={props.isDisabled || props.isPlusDisabled}
    >
      <span
        className={cn(styles.icon, showOutlinedStyle && styles.icon_primary)}
        data-testid="AddIcon"
      >
        <AddIcon />
      </span>
    </button>
  );

  const shouldShowControls =
    itemCount > 0 &&
    (!props.isDisabled ||
      props.changeCounterView === ChangeCounterFrom.ItemView ||
      props.changeCounterView === ChangeCounterFrom.AddOns);

  const controlsWrapper = (
    <div
      className={cn(
        styles.controls_wrapper,
        includeBackground && styles.controls_wrapper_background,
        shouldShowControls ? styles.controls_visible : styles.controls_hidden
      )}
    >
      {layout === "vertical" && (
        <span className={styles.count} role="counterNumber">
          {localizedNumber(Math.max(itemCount, 1))}
        </span>
      )}
      <button
        type="button"
        className={cn(
          styles.button,
          hasShadow && styles.shadow,
          includeBackground && styles.button_on_background,
          disabledButtonsColor === "primary" && styles.primary_disabled
        )}
        aria-label="minus"
        data-testid={`minus-${props.data?.itemDetails.item_name}`}
        onClick={() =>
          props.customOnClick
            ? props.customOnClick(EditCartItemEnum.CounterDecChange)
            : changeCount(EditCartItemEnum.CounterDecChange)
        }
        disabled={props.isDisabled || props.isMinusDisabled}
      >
        <span className={styles.icon} data-testid="MinusIcon">
          <MinusIcon />
        </span>
      </button>
      {layout === "horizontal" && (
        <span className={styles.count} role="counterNumber">
          {localizedNumber(Math.max(itemCount, 1))}
        </span>
      )}
    </div>
  );

  return (
    <div
      className={cn(
        styles.root,
        styles[size],
        styles[layout],
        props.isDisabled && styles.counter_disabled,
        hasShadow && styles.shadow,
        layout === "vertical" &&
          !shouldShowControls &&
          styles.vertical_only_plus,
        props.transparent && styles.transparent
      )}
    >
      {layout === "vertical" ? (
        <>
          {plusButton}
          {controlsWrapper}
        </>
      ) : (
        <>
          {controlsWrapper}
          {plusButton}
        </>
      )}
    </div>
  );
};
